import { useEffect, useState, useLayoutEffect } from "react";
import request from "project-config/services/request/request";
import { createContainer } from "unstated-next";
import { useQuery } from "react-query";
import {
  IAuthRecord,
  IAuthObjectPermissionMap,
} from "@/pages/authorization-management/types";
import { usePermissionStore } from "@/store/PermissionStore";
import { IAllPermission } from "@/store/PermissionStore";
import { AuthStatus } from "@/pages/authorization-management/constants/authStatus";

export function useAuthManageStoreHook() {
  const [openAuthStatementDialog, setOpenAuthStatementDialog] = useState(false);
  const [openEditAuthDrawer, setOpenEditAuthDrawer] = useState(false);
  const [checkAllPermission, setCheckAllPermission] = useState(false);
  const [authRecord, setAuthRecord] = useState<IAuthRecord>();
  const [authStatus, setAuthStatus] = useState(AuthStatus.Cancel);
  const [authObjectPermissionMap, setAuthObjectPermissionMap] =
    useState<IAuthObjectPermissionMap>({});
  const [allAuthObjectPermissionMap, setAllAuthObjectPermissionMap] =
    useState<IAuthObjectPermissionMap>({});
  const { allPermission } = usePermissionStore();

  async function fetchUserAuthRecord() {
    const { data } = await request.post(
      "/api/rendering/user/userAuth/queryLatelyUserAuthRecord"
    );
    if (!data) return;
    setAuthRecord(data);
  }
  const { data } = useQuery<IAuthRecord>(
    "/api/rendering/user/userAuth/queryLatelyUserAuthRecord"
  );

  async function closeAuth() {
    await request.post("/api/rendering/user/userAuth/closeUserAuth", {
      id: authRecord?.id,
    });
    await fetchUserAuthRecord();
    setAuthStatus(AuthStatus.Cancel);
  }

  function initAuthObjectPermissionMap() {
    const result: IAuthObjectPermissionMap = {};
    allPermission.forEach((module) => {
      module.children.forEach((authObject) => {
        result[String(authObject.id)] = [];
      });
    });
    return result;
  }

  const getAuthObjectPermissionMap = (permissionList: IAllPermission[]) => {
    if (!permissionList || !permissionList.length)
      return initAuthObjectPermissionMap();
    return permissionList.reduce((result, module: IAllPermission) => {
      // shit
      if (!module.children) {
        allPermission
          .find((_module) => _module.id === module.id)
          ?.children.forEach((_authObject) => {
            result[String(_authObject.id)] = [];
          });
        return result;
      }
      module.children.forEach((authObject) => {
        const permissionIds = authObject.children.map((p) => p.id);
        result[String(authObject.id)] = permissionIds;
      });
      return result;
    }, {} as { [key: string]: number[] });
  };

  const _setAuthObjectPermissionMap = (permissionList: IAllPermission[]) => {
    setAuthObjectPermissionMap(getAuthObjectPermissionMap(permissionList));
  };

  useEffect(() => {
    setAuthRecord(data);
    setAllAuthObjectPermissionMap(getAuthObjectPermissionMap(allPermission));
    if (!data) {
      setAuthObjectPermissionMap(getAuthObjectPermissionMap(allPermission));
      return;
    }
    setAuthObjectPermissionMap(getAuthObjectPermissionMap(data.permissionBos));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPermission, data]);

  useEffect(() => {
    if (!authRecord || authRecord.cancelFlag) {
      return;
    }
    setAuthObjectPermissionMap(
      getAuthObjectPermissionMap(authRecord.permissionBos)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRecord]);

  useEffect(() => {
    const authObjectIds = Object.keys(authObjectPermissionMap);
    const allAuthObjectIds = Object.keys(allAuthObjectPermissionMap);
    if (`${authObjectIds.sort()}` !== `${allAuthObjectIds.sort()}`) {
      setCheckAllPermission(false);
      return;
    }
    const currentThirdPermissionIds = Object.values(
      authObjectPermissionMap
    ).flat();
    const allThirdPermissionIds = Object.values(
      allAuthObjectPermissionMap
    ).flat();
    setCheckAllPermission(
      `${currentThirdPermissionIds.sort()}` ===
        `${allThirdPermissionIds.sort()}`
    );
  }, [authObjectPermissionMap, allAuthObjectPermissionMap]);

  useLayoutEffect(() => {
    if (!data || data.cancelFlag) {
      setAuthStatus(AuthStatus.Cancel);
    } else {
      setAuthStatus(
        data.endDate > +new Date() ? AuthStatus.Effective : AuthStatus.Expired
      );
    }
  }, [data]);

  return {
    openAuthStatementDialog,
    setOpenAuthStatementDialog,
    openEditAuthDrawer,
    setOpenEditAuthDrawer,
    fetchUserAuthRecord,
    authRecord,
    setAuthRecord,
    allAuthObjectPermissionMap,
    authObjectPermissionMap,
    setAuthObjectPermissionMap,
    _setAuthObjectPermissionMap,
    authStatus,
    setAuthStatus,
    closeAuth,
    checkAllPermission,
    setCheckAllPermission,
  };
}

const AuthManageStoreContainer = createContainer(useAuthManageStoreHook);

export const AuthManageStoreProvider = AuthManageStoreContainer.Provider;

export function useAuthManageStore() {
  return AuthManageStoreContainer.useContainer();
}
